import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  PhonebookContactVM,
  CreatePhonebookContactDTO,
  UpdatePhonebookContactDTO,
  PhonebookListResponseVM,
  PhonebookCountsVM,
  PhonebookListParams,
  ScanCardResult,
  PhonebookContactNoteVM,
  CreatePhonebookContactNoteDTO,
} from '../models';

@Injectable({ providedIn: 'root' })
export class PhonebookService {
  private readonly baseUrl = `${environment.apiConfig.corporateAPI.endpoint}/api/phonebook`;
  private readonly http = inject(HttpClient);

  list(params: PhonebookListParams = {}): Observable<PhonebookListResponseVM> {
    let httpParams = new HttpParams();

    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.type) {
      httpParams = httpParams.set('type', params.type);
    }
    if (params.page != null) {
      httpParams = httpParams.set('page', params.page.toString());
    }
    if (params.pageSize != null) {
      httpParams = httpParams.set('pageSize', params.pageSize.toString());
    }
    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
    }
    if (params.sortDirection) {
      httpParams = httpParams.set('sortDirection', params.sortDirection);
    }

    return this.http.get<PhonebookListResponseVM>(this.baseUrl, {
      params: httpParams,
    });
  }

  getById(id: number): Observable<PhonebookContactVM> {
    return this.http.get<PhonebookContactVM>(`${this.baseUrl}/${id}`);
  }

  create(dto: CreatePhonebookContactDTO): Observable<PhonebookContactVM> {
    return this.http.post<PhonebookContactVM>(this.baseUrl, dto);
  }

  update(id: number, dto: UpdatePhonebookContactDTO): Observable<PhonebookContactVM> {
    return this.http.put<PhonebookContactVM>(`${this.baseUrl}/${id}`, dto);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  getCounts(): Observable<PhonebookCountsVM> {
    return this.http.get<PhonebookCountsVM>(`${this.baseUrl}/counts`);
  }

  /** Find existing contacts matching an email or phone (duplicate check). */
  lookup(email?: string | null, phone?: string | null): Observable<PhonebookContactVM[]> {
    let params = new HttpParams();
    if (email) {
      params = params.set('email', email);
    }
    if (phone) {
      params = params.set('phone', phone);
    }
    return this.http.get<PhonebookContactVM[]>(`${this.baseUrl}/lookup`, { params });
  }

  /** Extract contact details from a business-card image via AI vision. */
  scanCard(file: File): Observable<ScanCardResult> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ScanCardResult>(`${this.baseUrl}/scan-card`, formData);
  }

  // ─── Notes thread ───

  listNotes(contactId: number): Observable<PhonebookContactNoteVM[]> {
    return this.http.get<PhonebookContactNoteVM[]>(`${this.baseUrl}/${contactId}/notes`);
  }

  addNote(contactId: number, dto: CreatePhonebookContactNoteDTO): Observable<PhonebookContactNoteVM> {
    return this.http.post<PhonebookContactNoteVM>(`${this.baseUrl}/${contactId}/notes`, dto);
  }

  deleteNote(contactId: number, noteId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${contactId}/notes/${noteId}`);
  }
}
